import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Settings, Shield, Globe, Calendar, ToggleLeft, ToggleRight,
    Save, RefreshCw, ChevronDown, ChevronRight, X, Check,
    Heart, Brain, BookOpen, MapPin, AlertCircle, Zap, LayoutDashboard, Trophy, Newspaper,
    Bot, TrendingUp, Loader2, Database, Flag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available countries for filtering
const AVAILABLE_COUNTRIES = [
    { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'IRE', name: 'Ireland', flag: '🇮🇪' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'USA', name: 'United States', flag: '🇺🇸' },
    { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
    { code: 'AUS', name: 'Australia', flag: '🇦🇺' },
];

// Feature definitions
const FEATURE_DEFINITIONS = [
    {
        key: 'home_page',
        name: 'Home Page',
        description: 'Show Home link in navigation (disable to make Competitions the landing page)',
        icon: LayoutDashboard
    },
    {
        key: 'competitions',
        name: 'Competitions (Horse Racing)',
        description: 'Show the Competitions (tipping) link in navigation',
        icon: Trophy
    },
    {
        key: 'races_page',
        name: 'Races Page',
        description: 'Show Races link in navigation',
        icon: Calendar
    },
    {
        key: 'racing_hub',
        name: 'Racing Hub',
        description: 'Racing Hub landing page — racing news and today\'s races',
        icon: Flag
    },
    { 
        key: 'my_stable', 
        name: 'My Stable', 
        description: 'Allow users to follow horses and track their favorites',
        icon: Heart
    },
    { 
        key: 'experts_vs_ai', 
        name: 'Podium vs AI', 
        description: 'Show Podium vs AI predictions comparison page',
        icon: Brain
    },
    { 
        key: 'predictions', 
        name: 'Predictions', 
        description: 'Fun prediction markets game with virtual points',
        icon: Zap
    },
    {
        key: 'learning_centre',
        name: 'Learning Centre',
        description: 'Educational content and betting guides',
        icon: BookOpen
    },
    {
        key: 'football_hub',
        name: 'Football Hub',
        description: 'Football Hub landing page — news, live scores, tables and top scorers',
        icon: Newspaper
    },
    {
        key: 'football_comps',
        name: 'Football Comps',
        description: 'Score prediction competitions (Premier League, World Cup, etc.)',
        icon: Globe
    },
    {
        key: 'stats_enrichment',
        name: 'Shot Map & xG Enrichment',
        description: 'Enable theStatsAPI shot maps and xG data on match pages (requires team mappings to be set up)',
        icon: Zap
    },
    {
        key: 'clarity',
        name: 'Clarity (Quick Form)',
        description: 'Show the Clarity factor scores tab on race cards — form, fitness, course, distance, potential and more',
        icon: LayoutDashboard
    },
];

// Every real, working page a user could land on. `featureKey` (if set) ties an
// option to a nav toggle above — hidden from the list whenever that feature is
// switched off, since pointing new visitors at a disabled page would be
// confusing. Pages with no featureKey (Settings, Bet History, World Cup) are
// always reachable regardless of feature toggles, so they're always offered.
const LANDING_PAGE_OPTIONS = [
    { path: '/', label: 'Home', featureKey: 'home_page' },
    { path: '/races', label: 'Races', featureKey: 'races_page' },
    { path: '/racing-hub', label: 'Racing Hub', featureKey: 'racing_hub' },
    { path: '/competitions', label: 'Competitions', featureKey: 'competitions' },
    { path: '/football-hub', label: 'Football Hub', featureKey: 'football_hub' },
    { path: '/football', label: 'Football Comps', featureKey: 'football_comps' },
    { path: '/predictions', label: 'Predictions', featureKey: 'predictions' },
    { path: '/experts-vs-ai', label: 'Podium vs AI', featureKey: 'experts_vs_ai' },
    { path: '/stable', label: 'My Stable', featureKey: 'my_stable' },
    { path: '/settings', label: 'Settings', featureKey: null },
    { path: '/bets', label: 'Bet History', featureKey: null },
    { path: '/wc2026', label: 'World Cup 2026', featureKey: null },
];

export default function AdminDashboardPage() {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    
    // Settings state
    const [featureToggles, setFeatureToggles] = useState({
        home_page: true,
        races_page: true,
        racing_hub: true,
        my_stable: true,
        competitions: true,
        experts_vs_ai: true,
        predictions: true,
        learning_centre: true,
        football_hub: true,
        football_comps: true,
        clarity: true
    });
    const [defaultPage, setDefaultPage] = useState('');
    const [disabledCountries, setDisabledCountries] = useState([]);
    const [disabledMeetings, setDisabledMeetings] = useState([]);
    
    // Meeting management state
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [meetingsForDate, setMeetingsForDate] = useState([]);
    const [loadingMeetings, setLoadingMeetings] = useState(false);
    const [expandedCountries, setExpandedCountries] = useState({});
    const [meetingsLoaded, setMeetingsLoaded] = useState(false);

    useEffect(() => {
        if (!isAuthenticated || !user?.is_admin) {
            toast.error('Admin access required');
            navigate('/');
            return;
        }
        fetchSettings();
    }, [isAuthenticated, user, navigate]);

    const fetchSettings = async () => {
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/admin/site-settings`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setFeatureToggles(res.data.feature_toggles || {
                home_page: true,
                races_page: true,
                racing_hub: true,
                my_stable: true,
                experts_vs_ai: true,
                predictions: true,
                learning_centre: true
            });
            setDefaultPage(res.data.default_page || '');
            setDisabledCountries(res.data.disabled_countries || []);
            setDisabledMeetings(res.data.disabled_meetings || []);
        } catch (err) {
            console.error('Failed to fetch settings:', err);
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const handleFeatureToggle = (featureKey) => {
        setFeatureToggles(prev => ({
            ...prev,
            [featureKey]: !prev[featureKey]
        }));
        setHasChanges(true);
    };

    const handleCountryToggle = (countryCode) => {
        setDisabledCountries(prev => {
            if (prev.includes(countryCode)) {
                return prev.filter(c => c !== countryCode);
            } else {
                return [...prev, countryCode];
            }
        });
        setHasChanges(true);
    };

    const saveSettings = async () => {
        setSaving(true);
        try {
            const token = getToken();
            await axios.put(`${API_URL}/api/admin/site-settings`, {
                feature_toggles: featureToggles,
                disabled_countries: disabledCountries,
                default_page: defaultPage || null,
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success('Settings saved successfully');
            setHasChanges(false);
        } catch (err) {
            console.error('Failed to save settings:', err);
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const fetchMeetingsForDate = async (date) => {
        setLoadingMeetings(true);
        try {
            const token = getToken();
            const formattedDate = format(date, 'dd-MM-yyyy');
            
            const res = await axios.get(`${API_URL}/api/admin/meetings-by-date/${formattedDate}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setMeetingsForDate(res.data.meetings || []);
            setMeetingsLoaded(true);
        } catch (err) {
            console.error('Failed to fetch meetings:', err);
            toast.error('Failed to load meetings for selected date');
            setMeetingsForDate([]);
        } finally {
            setLoadingMeetings(false);
        }
    };

    const handleDateSelect = (date) => {
        setSelectedDate(date);
        if (date) {
            fetchMeetingsForDate(date);
        }
    };

    const handleDisableMeeting = async (meeting) => {
        try {
            const token = getToken();
            const formattedDate = format(selectedDate, 'dd-MM-yyyy');
            
            await axios.post(`${API_URL}/api/admin/disable-meeting`, {
                date: formattedDate,
                meeting_id: meeting.meeting_id,
                meeting_name: meeting.name
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success(`${meeting.name} disabled`);
            // Refresh meetings list
            fetchMeetingsForDate(selectedDate);
            // Refresh main settings to update disabled meetings list
            fetchSettings();
        } catch (err) {
            console.error('Failed to disable meeting:', err);
            toast.error('Failed to disable meeting');
        }
    };

    const handleEnableMeeting = async (meeting) => {
        try {
            const token = getToken();
            const formattedDate = format(selectedDate, 'dd-MM-yyyy');
            
            await axios.delete(`${API_URL}/api/admin/enable-meeting/${formattedDate}/${meeting.meeting_id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success(`${meeting.name} enabled`);
            // Refresh meetings list
            fetchMeetingsForDate(selectedDate);
            // Refresh main settings
            fetchSettings();
        } catch (err) {
            console.error('Failed to enable meeting:', err);
            toast.error('Failed to enable meeting');
        }
    };

    const toggleCountryExpanded = (countryCode) => {
        setExpandedCountries(prev => ({
            ...prev,
            [countryCode]: !prev[countryCode]
        }));
    };

    // Group meetings by country
    const meetingsByCountry = meetingsForDate.reduce((acc, meeting) => {
        const country = meeting.country || 'OTHER';
        if (!acc[country]) acc[country] = [];
        acc[country].push(meeting);
        return acc;
    }, {});

    if (loading) {
        return (
            <div className="container-app py-8 space-y-6">
                <Skeleton className="h-10 w-64" />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Skeleton className="h-96" />
                    <Skeleton className="h-96" />
                </div>
            </div>
        );
    }

    return (
        <div className="container-app py-8 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                        <Shield className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold font-heading page-title">Admin Dashboard</h1>
                        <p className="text-sm text-muted-foreground">Manage site features and content visibility</p>
                    </div>
                </div>
                
                {hasChanges && (
                    <Button 
                        onClick={saveSettings} 
                        disabled={saving}
                        className="gap-2"
                        data-testid="save-settings-btn"
                    >
                        {saving ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="h-4 w-4" />
                        )}
                        Save Changes
                    </Button>
                )}
            </div>

            <Tabs defaultValue="features" className="space-y-6" onValueChange={(value) => {
                if (value === 'meetings' && !meetingsLoaded && selectedDate) {
                    fetchMeetingsForDate(selectedDate);
                }
            }}>
                <TabsList className="grid w-full max-w-2xl grid-cols-4">
                    <TabsTrigger value="features" data-testid="tab-features">
                        <Settings className="h-4 w-4 mr-2" />
                        Features
                    </TabsTrigger>
                    <TabsTrigger value="countries" data-testid="tab-countries">
                        <Globe className="h-4 w-4 mr-2" />
                        Countries
                    </TabsTrigger>
                    <TabsTrigger value="meetings" data-testid="tab-meetings">
                        <Calendar className="h-4 w-4 mr-2" />
                        Meetings
                    </TabsTrigger>
                    <TabsTrigger value="clarity-model" data-testid="tab-clarity-model">
                        <Bot className="h-4 w-4 mr-2" />
                        Clarity AI
                    </TabsTrigger>
                </TabsList>

                {/* Feature Toggles Tab */}
                <TabsContent value="features" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <ToggleLeft className="h-5 w-5" />
                                Feature Visibility
                            </CardTitle>
                            <CardDescription>
                                Enable or disable major site features. Disabled features will be hidden from navigation.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {FEATURE_DEFINITIONS.map((feature) => {
                                const Icon = feature.icon;
                                const isEnabled = featureToggles[feature.key] !== false;
                                
                                return (
                                    <div 
                                        key={feature.key}
                                        className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                                            isEnabled ? 'bg-background' : 'bg-muted/50 border-dashed'
                                        }`}
                                        data-testid={`feature-toggle-${feature.key}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-lg ${isEnabled ? 'bg-primary/10' : 'bg-muted'}`}>
                                                <Icon className={`h-5 w-5 ${isEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
                                            </div>
                                            <div>
                                                <h3 className="font-medium">{feature.name}</h3>
                                                <p className="text-sm text-muted-foreground">{feature.description}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Badge variant={isEnabled ? 'default' : 'secondary'}>
                                                {isEnabled ? 'Enabled' : 'Disabled'}
                                            </Badge>
                                            <Switch
                                                checked={isEnabled}
                                                onCheckedChange={() => handleFeatureToggle(feature.key)}
                                                data-testid={`switch-${feature.key}`}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <LayoutDashboard className="h-5 w-5" />
                                Default Landing Page
                            </CardTitle>
                            <CardDescription>
                                Tick which page users land on when clicking the logo or navigating to the root URL. Untick a page to go back to Auto (first enabled nav item). Only enabled pages are available.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-0.5">
                            {LANDING_PAGE_OPTIONS
                                .filter(opt => !opt.featureKey || featureToggles[opt.featureKey] !== false)
                                .map(opt => (
                                    <label
                                        key={opt.path}
                                        className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted/50 cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-input accent-primary cursor-pointer"
                                            checked={defaultPage === opt.path}
                                            onChange={() => {
                                                setDefaultPage(defaultPage === opt.path ? '' : opt.path);
                                                setHasChanges(true);
                                            }}
                                        />
                                        <span className="text-sm font-medium">{opt.label}</span>
                                        <code className="text-xs text-muted-foreground ml-auto">{opt.path}</code>
                                    </label>
                                ))}
                            {!defaultPage && (
                                <p className="text-xs text-muted-foreground pt-2">
                                    Currently: Auto (first enabled page in the nav)
                                </p>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Countries Tab */}
                <TabsContent value="countries" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Globe className="h-5 w-5" />
                                Country Restrictions
                            </CardTitle>
                            <CardDescription>
                                Disable countries to hide all races from those regions. Users will not see any meetings from disabled countries.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {AVAILABLE_COUNTRIES.map((country) => {
                                    const isDisabled = disabledCountries.includes(country.code);
                                    
                                    return (
                                        <div 
                                            key={country.code}
                                            className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                                                isDisabled 
                                                    ? 'bg-destructive/10 border-destructive/30' 
                                                    : 'bg-background hover:bg-muted/50'
                                            }`}
                                            onClick={() => handleCountryToggle(country.code)}
                                            data-testid={`country-toggle-${country.code}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="text-2xl">{country.flag}</span>
                                                <div>
                                                    <p className="font-medium">{country.name}</p>
                                                    <p className="text-xs text-muted-foreground">{country.code}</p>
                                                </div>
                                            </div>
                                            <div className={`p-1 rounded-full ${isDisabled ? 'bg-destructive/20' : 'bg-green-500/20'}`}>
                                                {isDisabled ? (
                                                    <X className="h-4 w-4 text-destructive" />
                                                ) : (
                                                    <Check className="h-4 w-4 text-green-600" />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            
                            {disabledCountries.length > 0 && (
                                <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                                        <div className="text-sm">
                                            <p className="font-medium text-amber-700 dark:text-amber-400">
                                                {disabledCountries.length} {disabledCountries.length === 1 ? 'country' : 'countries'} disabled
                                            </p>
                                            <p className="text-amber-600/80 dark:text-amber-400/80">
                                                Races from {disabledCountries.join(', ')} will be hidden from all users.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Meetings Tab */}
                <TabsContent value="meetings" className="space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Calendar Section */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Calendar className="h-5 w-5" />
                                    Select Date
                                </CardTitle>
                                <CardDescription>
                                    Choose a date to manage meetings
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="flex justify-center">
                                <CalendarComponent
                                    mode="single"
                                    selected={selectedDate}
                                    onSelect={handleDateSelect}
                                    className="rounded-md border"
                                    data-testid="meeting-calendar"
                                />
                            </CardContent>
                        </Card>

                        {/* Meetings List */}
                        <Card className="lg:col-span-2">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <MapPin className="h-5 w-5" />
                                            Meetings for {selectedDate ? format(selectedDate, 'dd MMM yyyy') : 'Selected Date'}
                                        </CardTitle>
                                        <CardDescription>
                                            Toggle individual meetings on or off for the selected date
                                        </CardDescription>
                                    </div>
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => fetchMeetingsForDate(selectedDate)}
                                        disabled={!selectedDate || loadingMeetings}
                                        data-testid="refresh-meetings-btn"
                                    >
                                        <RefreshCw className={`h-4 w-4 mr-2 ${loadingMeetings ? 'animate-spin' : ''}`} />
                                        Refresh
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {loadingMeetings ? (
                                    <div className="space-y-3">
                                        {[1, 2, 3].map(i => (
                                            <Skeleton key={i} className="h-16 w-full" />
                                        ))}
                                    </div>
                                ) : meetingsForDate.length === 0 ? (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                        <p>No meetings found for this date</p>
                                        <p className="text-sm">Select a date with scheduled races</p>
                                    </div>
                                ) : (
                                    <ScrollArea className="h-[400px] pr-4">
                                        <div className="space-y-4">
                                            {Object.entries(meetingsByCountry).map(([countryCode, meetings]) => {
                                                const countryInfo = AVAILABLE_COUNTRIES.find(c => c.code === countryCode) || { 
                                                    code: countryCode, 
                                                    name: countryCode, 
                                                    flag: '🏳️' 
                                                };
                                                const isExpanded = expandedCountries[countryCode] !== false;
                                                
                                                return (
                                                    <Collapsible 
                                                        key={countryCode} 
                                                        open={isExpanded}
                                                        onOpenChange={() => toggleCountryExpanded(countryCode)}
                                                    >
                                                        <CollapsibleTrigger className="w-full">
                                                            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xl">{countryInfo.flag}</span>
                                                                    <span className="font-medium">{countryInfo.name}</span>
                                                                    <Badge variant="secondary" className="ml-2">
                                                                        {meetings.length} meetings
                                                                    </Badge>
                                                                </div>
                                                                {isExpanded ? (
                                                                    <ChevronDown className="h-4 w-4" />
                                                                ) : (
                                                                    <ChevronRight className="h-4 w-4" />
                                                                )}
                                                            </div>
                                                        </CollapsibleTrigger>
                                                        <CollapsibleContent className="mt-2 space-y-2 pl-4">
                                                            {meetings.map((meeting) => (
                                                                <div 
                                                                    key={meeting.meeting_id}
                                                                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                                                                        meeting.is_disabled 
                                                                            ? 'bg-destructive/10 border-destructive/30' 
                                                                            : 'bg-background'
                                                                    }`}
                                                                    data-testid={`meeting-item-${meeting.meeting_id}`}
                                                                >
                                                                    <div>
                                                                        <p className={`font-medium ${meeting.is_disabled ? 'line-through text-muted-foreground' : ''}`}>
                                                                            {meeting.name}
                                                                        </p>
                                                                        <p className="text-xs text-muted-foreground">
                                                                            {meeting.race_count} races
                                                                            {meeting.first_race_time && ` • First race: ${format(parseISO(meeting.first_race_time), 'HH:mm')}`}
                                                                        </p>
                                                                    </div>
                                                                    <Button
                                                                        variant={meeting.is_disabled ? 'outline' : 'destructive'}
                                                                        size="sm"
                                                                        onClick={() => meeting.is_disabled 
                                                                            ? handleEnableMeeting(meeting) 
                                                                            : handleDisableMeeting(meeting)
                                                                        }
                                                                        data-testid={`meeting-toggle-${meeting.meeting_id}`}
                                                                    >
                                                                        {meeting.is_disabled ? (
                                                                            <>
                                                                                <Check className="h-4 w-4 mr-1" />
                                                                                Enable
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <X className="h-4 w-4 mr-1" />
                                                                                Disable
                                                                            </>
                                                                        )}
                                                                    </Button>
                                                                </div>
                                                            ))}
                                                        </CollapsibleContent>
                                                    </Collapsible>
                                                );
                                            })}
                                        </div>
                                    </ScrollArea>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Currently Disabled Meetings Summary */}
                    {disabledMeetings.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Currently Disabled Meetings</CardTitle>
                                <CardDescription>
                                    These meetings are hidden from users across all dates
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-wrap gap-2">
                                    {disabledMeetings.map((meeting, idx) => (
                                        <Badge 
                                            key={`${meeting.meeting_id}-${idx}`}
                                            variant="secondary"
                                            className="gap-1"
                                        >
                                            {meeting.meeting_name} ({meeting.date})
                                        </Badge>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                {/* Clarity AI Tab */}
                <ClarityModelTab />
            </Tabs>
        </div>
    );
}

// ── Clarity Model Admin ───────────────────────────────────────────────────────

function ClarityModelTab() {
    const [model, setModel] = useState(null);
    const [training, setTraining] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncDate, setSyncDate] = useState('');
    const [syncing, setSyncing] = useState(false);
    const [trainLoading, setTrainLoading] = useState(false);
    const [applying, setApplying] = useState(false);

    const token = getToken();
    const headers = { Authorization: `Bearer ${token}` };

    const loadModel = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/admin/clarity/model`, { headers });
            setModel(res.data.model);
            setTraining(res.data.training);
        } catch {
            toast.error('Failed to load Clarity model data');
        } finally {
            setLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadModel(); }, [loadModel]);

    const handleSync = async () => {
        setSyncing(true);
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/clarity/sync-results`,
                syncDate ? { date: syncDate } : {},
                { headers },
            );
            toast.success(`Stored ${res.data.samples_stored} samples from ${res.data.date}`);
            loadModel();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Sync failed');
        } finally {
            setSyncing(false);
        }
    };

    const handleTrain = async () => {
        setTrainLoading(true);
        try {
            const res = await axios.post(`${API_URL}/api/admin/clarity/train`, {}, { headers });
            if (res.data.ok) {
                toast.success(`Model trained on ${res.data.samples} samples — accuracy ${Math.round(res.data.train_accuracy * 100)}%`);
            } else {
                toast.error(res.data.reason || 'Training failed');
            }
            loadModel();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Training failed');
        } finally {
            setTrainLoading(false);
        }
    };

    const handleApply = async (apply) => {
        setApplying(true);
        try {
            const url = apply ? `${API_URL}/api/admin/clarity/weights/apply` : `${API_URL}/api/admin/clarity/weights/reset`;
            await axios.post(url, {}, { headers });
            toast.success(apply ? 'Learned weights applied' : 'Reverted to default weights');
            loadModel();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        } finally {
            setApplying(false);
        }
    };

    const defaultWeights = {
        form: 0.22, going: 0.14, fitness: 0.14, jockey: 0.12,
        distance: 0.11, course: 0.10, potential: 0.09, trainer: 0.05, equipment: 0.03,
    };

    if (loading) {
        return (
            <TabsContent value="clarity-model" className="space-y-4">
                <Skeleton className="h-40 w-full" />
                <Skeleton className="h-40 w-full" />
            </TabsContent>
        );
    }

    return (
        <TabsContent value="clarity-model" className="space-y-4">
            {/* Dataset */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Database className="h-5 w-5" /> Training Dataset
                    </CardTitle>
                    <CardDescription>
                        Historical race results scored with Clarity. Sync past dates to build the dataset.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="p-3 rounded-lg bg-muted">
                            <p className="text-2xl font-bold">{training?.total_samples ?? 0}</p>
                            <p className="text-xs text-muted-foreground mt-1">Total samples</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted">
                            <p className="text-2xl font-bold">{training?.winners ?? 0}</p>
                            <p className="text-xs text-muted-foreground mt-1">Winners stored</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted">
                            <p className="text-2xl font-bold">{training?.races ?? 0}</p>
                            <p className="text-xs text-muted-foreground mt-1">Races covered</p>
                        </div>
                    </div>
                    <div className="flex gap-2 items-center">
                        <input
                            type="date"
                            value={syncDate}
                            onChange={e => setSyncDate(e.target.value)}
                            className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                            placeholder="yesterday by default"
                        />
                        <Button onClick={handleSync} disabled={syncing} size="sm">
                            {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                            Sync Results
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Model */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" /> Learned Weights
                    </CardTitle>
                    <CardDescription>
                        Train a logistic regression model on the dataset to discover empirically optimal factor weights.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {model ? (
                        <>
                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                                <span>Trained on <strong className="text-foreground">{model.samples}</strong> samples</span>
                                <span>Train accuracy <strong className="text-foreground">{Math.round(model.train_accuracy * 100)}%</strong></span>
                                <span>Status: <strong className={model.use_learned ? 'text-green-500' : 'text-muted-foreground'}>{model.use_learned ? 'Active' : 'Standby'}</strong></span>
                            </div>

                            {/* Factor weight comparison */}
                            <div className="space-y-2">
                                {Object.keys(defaultWeights).map(key => {
                                    const def = defaultWeights[key];
                                    const learned = model.weights?.[key] ?? def;
                                    const diff = ((learned - def) * 100).toFixed(1);
                                    const isHigher = learned > def;
                                    return (
                                        <div key={key} className="flex items-center gap-2 text-xs">
                                            <span className="w-20 capitalize text-muted-foreground">{key}</span>
                                            <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                                                <div
                                                    className="h-full bg-primary rounded-full"
                                                    style={{ width: `${Math.round(learned * 100)}%` }}
                                                />
                                            </div>
                                            <span className="w-10 text-right font-mono">{Math.round(learned * 100)}%</span>
                                            <span className={`w-14 text-right font-mono text-[10px] ${isHigher ? 'text-green-500' : 'text-red-400'}`}>
                                                {isHigher ? '+' : ''}{diff}pp
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="flex gap-2 pt-2">
                                {!model.use_learned ? (
                                    <Button size="sm" onClick={() => handleApply(true)} disabled={applying} className="bg-green-600 hover:bg-green-700 text-white">
                                        {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                                        Apply learned weights
                                    </Button>
                                ) : (
                                    <Button size="sm" variant="outline" onClick={() => handleApply(false)} disabled={applying}>
                                        {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                                        Revert to defaults
                                    </Button>
                                )}
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-muted-foreground">No model trained yet. Sync some results then click Train.</p>
                    )}

                    <Button onClick={handleTrain} disabled={trainLoading} size="sm" variant="outline">
                        {trainLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bot className="h-4 w-4 mr-2" />}
                        Train model
                    </Button>
                </CardContent>
            </Card>
        </TabsContent>
    );
}
